import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  InvestorMarker,
  InvestorMarkerAddPayload,
  Marker,
} from "../api/transformers/marker";
import { useFormik } from "formik";
import { InvestorName } from "../model";
import useBoolean from "../hooks/useBoolean";
import { MarkerAPI } from "../api";
import { Option } from "../model";
import {
  MarkerGroupNamings,
  markerGroupOptions,
  MarkerGroups,
} from "../constant/markerConstant";
import LoadingTextField from "../components/LoadingTextField";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import { fullName } from "../utils/investor";
import { LoadingButton } from "@mui/lab";
import { useAuth } from "app/authHooks";
import { formatDate } from "../utils/date";

type MarkerFormConfig = {
  mode: "add" | "edit";
  title: string;
  allowRemove: boolean;
  marker: {
    label: string;
    groupCode: string;
    notInHistory: boolean;
    editable: boolean;
  };
  markerGroup: {
    editable: boolean;
    show: boolean;
  };
  submitText: string;
};

type Initialvalue = Partial<InvestorMarker> & {
  investor: InvestorName & { personId: string };
};

type SuccessPayload = {
  previous?: string;
  current?: string;
};

type MarkerDialogProps = {
  open: boolean;
  formConfig: MarkerFormConfig;
  initialValue: Initialvalue;
  markers: Marker[];
  onClose: () => void;
  onSuccess?: (payload: SuccessPayload) => void;
  onPreconditionCheck?: (code: string) => boolean;
};

type MarkerOption = Option & { disabled: boolean };

const validationSchema = yup.object({
  investor: yup.object().required("This Field is Required."),
  code: yup.string().required("This Field is Required."),
  groupCode: yup.string().required("This Field is Required."),
  comments: yup.string(),
});

const MarkerDialog: React.FC<MarkerDialogProps> = ({
  open,
  formConfig,
  initialValue,
  markers,
  onClose,
  onSuccess,
  onPreconditionCheck,
}) => {
  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: (values) => {
      if (onPreconditionCheck && !onPreconditionCheck(values.code!)) {
        return;
      }
      handleAddInvestorMarker(values);
    },
  });
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const [markerOptions, setMarkerOptions] = React.useState<MarkerOption[]>([]);
  const [
    markerHistoryLoading,
    {
      setTrue: startLoadingMarkerHistory,
      setFalse: finishLoadingMarkerHistory,
    },
  ] = useBoolean();
  const [markerHistory, setMarkerHistory] = React.useState<InvestorMarker[]>(
    []
  );
  React.useLayoutEffect(() => {
    if (open) {
      if (initialValue.investor && formConfig.marker.notInHistory) {
        getMarkerHistory(initialValue.investor.personId);
      } else {
        setMarkerOptions(
          getInitialMarkerOptions(
            markers,
            initialValue.groupCode,
            initialValue.code,
            markerHistory
          )
        );
      }
      formik.setValues({
        investor: initialValue.investor,
        code: initialValue.code,
        groupCode: initialValue.groupCode,
        dateAdded: initialValue.dateAdded,
        addedBy: initialValue.addedBy,
        comments: initialValue.comments,
      });
    } else {
      setMarkerHistory([]);
    }
  }, [open]);
  const handleSubmit = () => {
    formik.submitForm();
  };
  const handleUpdateInvestorMarker = (values: Initialvalue) => {
    const payload = {
      comments: values.comments,
    };
    startSubmitting();
    MarkerAPI.updateInvestorMarker(
      values.investor.personId,
      values.code!,
      payload
    )
      .then(() => {
        enqueueSnackbar("Successfully Submitted.", { variant: "success" });
        onSuccess &&
          onSuccess({
            previous: initialValue.code,
            current: values.code!,
          });
      })
      .catch(() => {
        enqueueSnackbar("Error! We couldn't submit the details.", {
          variant: "error",
        });
      })
      .finally(() => {
        finishSubmitting();
      });
  };

  const handleAddInvestorMarker = (values: Initialvalue) => {
    const payload: Partial<InvestorMarkerAddPayload> = buildPayload(values);
    if (initialValue.code) {
      payload.previousMarkerCode = initialValue.code;
    }
    payload.groupCode =
      values.groupCode === MarkerGroups.NONE ? "" : payload.groupCode;
    startSubmitting();
    MarkerAPI.addInvestorMarker(values.investor.personId, payload)
      .then(() => {
        enqueueSnackbar("Successfully Submitted.", { variant: "success" });
        onSuccess &&
          onSuccess({
            previous: initialValue.code,
            current: values.code!,
          });
      })
      .catch(() => {
        enqueueSnackbar("Error! We couldn't submit the details.", {
          variant: "error",
        });
      })
      .finally(() => {
        finishSubmitting();
      });
  };

  const handleDeleteInvestorMarker = () => {
    startSubmitting();
    MarkerAPI.removeInvestorMarker(
      initialValue.investor.personId,
      initialValue.code!
    )
      .then(() => {
        enqueueSnackbar("Successfully Removed.", { variant: "success" });
        onSuccess &&
          onSuccess({
            previous: initialValue.code,
          });
      })
      .catch(() => {
        enqueueSnackbar("Error! We couldn't remove the marker.", {
          variant: "error",
        });
      })
      .finally(() => {
        finishSubmitting();
      });
  };
  const getMarkerHistory = (personId: string) => {
    startLoadingMarkerHistory();
    return MarkerAPI.getMarkersOfInvestor(personId, markers)
      .then((response) => {
        const activeMarkers = response.filter((el) => !el.removed);
        if (activeMarkers.some((el) => el.code === formik.values.code)) {
          formik.setFieldValue("code", undefined);
        }
        setMarkerHistory(activeMarkers);
        setMarkerOptions(
          getInitialMarkerOptions(
            markers,
            formik.values.groupCode,
            initialValue.code,
            activeMarkers
          )
        );
      })
      .finally(() => {
        finishLoadingMarkerHistory();
      });
  };

  const renderInvestorFormItem = () => {
    const investorValue = formik.values?.investor
      ? `${fullName(formik.values.investor)} (${
        formik.values.investor.personId
      })`
      : "";
    return (
      <Box
        sx={{
          cursor: "pointer",
          pointerEvents: "auto",
          "& .MuiInputBase-root": {
            pointerEvents: "none",
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={investorValue}
          label="Investor"
          disabled
          InputProps={{
            readOnly: true,
          }}
        />
      </Box>
    );
  };
  const renderMarkerGroupFormItem = () => {
    if (!formConfig.markerGroup.show) {
      return null;
    }
    return (
      <LoadingTextField
        label={formConfig.marker.label}
        select
        size="small"
        name="groupCode"
        loading={markerHistoryLoading}
        value={formik.values?.groupCode}
        placeholder="select marker group"
        disabled={submitting || !formConfig.markerGroup.editable}
        onChange={formik.handleChange}
        error={formik.touched.groupCode && Boolean(formik.errors.groupCode)}
        helperText={formik.touched.groupCode && formik.errors.groupCode}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                maxHeight: 300,
                p: 0,
              },
            },
          },
        }}
      >
        {markerGroupOptions.map((el) => (
          <MenuItem value={el.value}>{el.label}</MenuItem>
        ))}
      </LoadingTextField>
    );
  };
  const renderMarkerFormItem = () => {
    return (
      <LoadingTextField
        label={formConfig.marker.label}
        disabled={!formConfig.marker.editable}
        select
        name="code"
        size="small"
        value={formik.values?.code}
        loading={markerHistoryLoading}
        onChange={formik.handleChange}
        error={formik.touched.code && Boolean(formik.errors.code)}
        helperText={formik.touched.code && formik.errors.code}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                maxHeight: 300,
                p: 0,
              },
            },
          },
        }}
      >
        {markerOptions.map((el) => (
          <MenuItem value={el.value} disabled={el.disabled}>
            {el.label}
          </MenuItem>
        ))}
      </LoadingTextField>
    );
  };
  const renderAddedFormItem = () => {
    return (
      <Stack direction="row" columnGap="10px">
        <TextField
          name="dateAdded"
          label="Date Added"
          size="small"
          disabled
          sx={{ flexGrow: 1 }}
          value={
            formik.values?.dateAdded ? formatDate(formik.values.dateAdded) : ""
          }
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
        />
        <TextField
          name="addedBy"
          label="Added By"
          size="small"
          disabled
          sx={{ flexGrow: 1 }}
          value={formik.values?.addedBy}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
        />
      </Stack>
    );
  };
  const renderCommentFormItem = () => {
    return (
      <TextField
        label="Comments"
        name="comments"
        value={formik.values?.comments}
        multiline
        rows={3}
        size="small"
        inputProps={{ maxLength: 200 }}
        placeholder="add some comments here"
        disabled={submitting}
        onChange={formik.handleChange}
        error={formik.touched.comments && Boolean(formik.errors.comments)}
        helperText={formik.touched.comments && formik.errors.comments}
      />
    );
  };
  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{
        sx: {
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>{formConfig?.title}</DialogTitle>
      <DialogContent>
        <form>
          <Stack
            direction="column"
            rowGap="12px"
            sx={{ width: "100%", marginTop: "10px" }}
          >
            {renderInvestorFormItem()}
            {renderMarkerGroupFormItem()}
            {renderMarkerFormItem()}
            {renderAddedFormItem()}
            {renderCommentFormItem()}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row-reverse"
          justifyContent="space-between"
          sx={{ width: "100%", padding: "0 16px 10px" }}
        >
          <Stack direction="row" columnGap="8px">
            <Button
              variant="text"
              sx={{
                backgroundColor: "transparent",
                color: "primary.main",
                padding: "6px 8px",
                minWidth: "64px",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.75,
                borderRadius: "4px",
                textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  boxShadow: "none",
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              sx={{ borderRadius: "4px" }}
              onClick={handleSubmit}
              loading={submitting || markerHistoryLoading}
            >
              {formConfig.submitText}
            </LoadingButton>
          </Stack>
          {formConfig.allowRemove && (
            <LoadingButton
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                color: (theme) => theme.palette.error.main,
                borderColor: (theme) => theme.palette.error.main,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.error.light,
                  borderColor: (theme) => theme.palette.error.dark,
                  color: "white",
                  boxShadow: "none",
                },
              }}
              variant="outlined"
              startIcon={<DeleteIcon />}
              loading={submitting}
              onClick={handleDeleteInvestorMarker}
            >
              Delete
            </LoadingButton>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

function getInitialMarkerOptions(
  markers: Marker[],
  currentMarkerGroup?: string,
  currentMarkerCode?: string,
  markerHistory: InvestorMarker[] = []
): MarkerOption[] {
  let markerOptions = markers;
  if (currentMarkerGroup) {
    markerOptions = markerOptions.filter(
      (el) => el.groupCode === currentMarkerGroup
    );
    if (currentMarkerCode) {
      markerOptions = markerOptions.filter(
        (el) => el.userSelectable || el.code === currentMarkerCode
      );
    } else {
      markerOptions = markerOptions.filter((el) => el.userSelectable);
    }
    return markerOptions.map((el) => {
      const markerExist = markerHistory.some((m) => m.code === el.code);
      return {
        value: el.code,
        label: el.description,
        disabled: !el.userSelectable || markerExist,
      };
    });
  } else {
    return [];
  }
}

function buildPayload(values: Initialvalue) {
  return {
    markerCode: values.code,
    groupCode: values.groupCode,
    comment: values.comments || "",
  };
}

export const useMarkerDialog = () => {
  const [open, { setTrue: showDialog, setFalse: closeDialog }] = useBoolean();
  const { attributes } = useAuth();
  const [formConfig, setFormConfig] = React.useState<MarkerFormConfig>({
    mode: "add",
    title: "",
    allowRemove: false,
    marker: {
      label: "",
      groupCode: "",
      notInHistory: false,
      editable: false,
    },
    markerGroup: {
      show: false,
      editable: false,
    },
    submitText: "Submit",
  });
  const [initialValue, setInitialValue] = React.useState<Initialvalue | null>(
    null
  );
  const openGroupMarkerAddDialog = (
    groupCode: string,
    investor: InvestorName & { personId: string }
  ) => {
    setFormConfig({
      mode: "add",
      title: "Add Marker",
      allowRemove: false,
      marker: {
        label: MarkerGroupNamings[groupCode as keyof typeof MarkerGroupNamings],
        groupCode,
        notInHistory: false,
        editable: true,
      },
      markerGroup: {
        show: false,
        editable: false,
      },
      submitText: "Add",
    });
    setInitialValue({
      investor,
      groupCode,
      addedBy: attributes["name"],
      dateAdded: format(new Date(), "yyyyMMdd"),
    });
    showDialog();
  };
  const openMarkerAddDialog = (
    markerCode: string,
    investor: InvestorName & { personId: string }
  ) => {
    setFormConfig({
      mode: "add",
      title: "Add Marker",
      allowRemove: false,
      marker: {
        label: "Marker Code",
        groupCode: MarkerGroups.NONE,
        notInHistory: false,
        editable: false,
      },
      markerGroup: {
        show: false,
        editable: false,
      },
      submitText: "Add",
    });
    setInitialValue({
      investor,
      groupCode: MarkerGroups.NONE,
      code: markerCode,
      addedBy: attributes["name"],
      dateAdded: format(new Date(), "yyyyMMdd"),
    });
    showDialog();
  };
  const openMarkerUpdateDialog = (
    marker: InvestorMarker,
    investor: InvestorName & { personId: string }
  ) => {
    const ofNoneGroup = marker.groupCode === MarkerGroups.NONE;
    setFormConfig({
      mode: "edit",
      title: ofNoneGroup
        ? "Update Marker"
        : `Update ${marker.groupDescription}`,
      allowRemove: ofNoneGroup,
      marker: {
        label: ofNoneGroup ? "Marker Code" : marker.groupDescription,
        groupCode: marker.groupCode,
        notInHistory: false,
        editable: !ofNoneGroup,
      },
      markerGroup: {
        show: false,
        editable: false,
      },
      submitText: "Update",
    });
    setInitialValue({
      investor,
      groupCode: marker.groupCode,
      addedBy: marker.addedBy,
      dateAdded: marker.dateAdded,
      comments: marker.comments,
      code: marker.code,
    });
    showDialog();
  };
  return {
    open,
    formConfig,
    initialValue,
    closeDialog,
    openGroupMarkerAddDialog,
    openMarkerAddDialog,
    openMarkerUpdateDialog,
  };
};

export default MarkerDialog;
